import { tags } from '@hungryroot/tags';
import { type Filter, type FilterGroup } from '../defs';

// Meals = catalog category 8 (ready-to-eat). Always sent; default personalized sort.
const BASE_PARAMS: ReadonlyArray<readonly [string, string]> = [
  ['categories', '8'],
  ['sort_by_default', '1'],
];

// Hardcoded meal filters. Values are copied verbatim from the grocery catalog
// (ProductFiltersV2) because the products endpoint filters by these specific
// keys — there is no generic tag filter. This config is the single seam to swap
// to an API-driven source later.
//
// Shared by the checked-out `/browse/meals/` page and the pre-checkout
// `/discover/meals/` mirror. The URL helpers below default to the full group;
// a surface that renders a subset passes its own `FilterGroup` so a filter it
// does not show can neither be read from nor written to its URL.
function mf(init: Partial<Filter>): Filter {
  return {
    title: '',
    queryKey: null,
    queryValue: '1',
    requestKey: null,
    requestValue: '1',
    pillText: null,
    pillOrder: 0,
    menuOrder: 0,
    hidden: false,
    tag: null,
    type: 'toggle',
    options: [],
    ...init,
  };
}

function protein(title: string, id: number, order: number): Filter {
  return mf({
    title,
    tag: id,
    requestKey: 'protein_tags',
    requestValue: String(id),
    queryKey: 'protein',
    queryValue: String(id),
    pillOrder: order,
    menuOrder: order,
  });
}

export const mealFilters: FilterGroup = {
  topFilters: [],
  query: '',
  bottomFilters: [
    mf({
      title: 'Dietary Needs',
      pillText: 'Diet',
      type: 'and',
      pillOrder: 1,
      menuOrder: 1,
      options: [
        mf({
          title: 'Vegan',
          requestKey: 'is_vegan',
          queryKey: 'is_vegan',
          pillOrder: 1,
          menuOrder: 1,
        }),
        mf({
          title: 'Vegetarian',
          requestKey: 'is_vegetarian',
          queryKey: 'is_vegetarian',
          pillOrder: 2,
          menuOrder: 2,
        }),
        mf({
          title: 'Pescatarian',
          requestKey: 'is_pescatarian',
          queryKey: 'is_pescatarian',
          pillOrder: 3,
          menuOrder: 3,
        }),
        mf({
          title: 'Dairy-Free',
          requestKey: 'is_dairy_free',
          queryKey: 'is_dairy_free',
          pillOrder: 4,
          menuOrder: 4,
        }),
        mf({
          title: 'Gluten-Free',
          requestKey: 'is_gluten_free',
          queryKey: 'is_gluten_free',
          pillOrder: 5,
          menuOrder: 5,
        }),
        mf({
          title: 'Soy-Free',
          requestKey: 'is_soy_free',
          queryKey: 'is_soy_free',
          pillOrder: 6,
          menuOrder: 6,
        }),
        mf({
          title: 'Tree Nut-Free',
          requestKey: 'is_nut_free',
          queryKey: 'is_nut_free',
          pillOrder: 7,
          menuOrder: 7,
        }),
        mf({
          title: 'Peanut-Free',
          requestKey: 'is_peanut_free',
          queryKey: 'is_peanut_free',
          pillOrder: 8,
          menuOrder: 8,
        }),
        mf({
          title: 'Egg-Free',
          requestKey: 'is_egg_free',
          queryKey: 'is_egg_free',
          pillOrder: 9,
          menuOrder: 9,
        }),
        mf({
          title: 'Shellfish-Free',
          requestKey: 'is_shellfish_free',
          queryKey: 'is_shellfish_free',
          pillOrder: 10,
          menuOrder: 10,
        }),
      ],
    }),
    mf({
      title: 'Protein',
      pillText: 'Protein',
      type: 'or',
      pillOrder: 2,
      menuOrder: 2,
      options: [
        protein('Ground Beef', tags.GroundBeefPrimary, 1),
        protein('Steak', tags.SteakPrimary, 2),
        protein('Bison', tags.BisonPrimary, 3),
        protein('Chicken', tags.ChickenPrimary, 4),
        protein('Turkey', tags.TurkeyPrimary, 5),
        protein('Pork', tags.PorkPrimary, 6),
        protein('Lamb', tags.LambPrimary, 7),
        protein('Salmon', tags.SalmonPrimary, 8),
        protein('Tuna', tags.TunaPrimary, 9),
        protein('Trout', tags.TroutPrimary, 10),
        protein('Whitefish', tags.WhitefishPrimary, 11),
        protein('Shellfish', tags.ShellfishPrimary, 12),
        protein('Smoked Salmon', tags.SmokedSalmonPrimary, 13),
        protein('Tofu + Tempeh', tags.TofuAndTempehPrimary, 14),
        protein('Beans + Lentils', tags.BeansAndLentilsPrimary, 15),
        protein(
          "Plant-based 'Meat' Alternatives",
          tags.MeatAlternativesPrimary,
          16
        ),
      ],
    }),
    mf({
      title: 'Deals',
      type: 'toggle',
      requestKey: 'sale',
      requestValue: '1',
      queryKey: 'is_deal',
      queryValue: '1',
      pillOrder: 3,
      menuOrder: 3,
    }),
    mf({
      title: 'Nutrition',
      type: 'and',
      pillOrder: 4,
      menuOrder: 4,
      options: [
        mf({
          title: 'Carb Conscious (<10g)',
          tag: 23,
          requestKey: 'nutrition_tags',
          requestValue: '23',
          queryKey: 'is_carb_conscious',
          pillOrder: 1,
          menuOrder: 1,
        }),
        mf({
          title: 'Less Sugar (<2g)',
          tag: 24,
          requestKey: 'nutrition_tags',
          requestValue: '24',
          queryKey: 'is_less_sugar',
          pillOrder: 2,
          menuOrder: 2,
        }),
        mf({
          title: 'High Protein (>10g)',
          tag: 25,
          requestKey: 'nutrition_tags',
          requestValue: '25',
          queryKey: 'is_high_protein',
          pillOrder: 3,
          menuOrder: 3,
        }),
        mf({
          title: 'Less Sodium (<140mg)',
          tag: 26,
          requestKey: 'nutrition_tags',
          requestValue: '26',
          queryKey: 'is_less_sodium',
          pillOrder: 4,
          menuOrder: 4,
        }),
        mf({
          title: 'No Added Sugar',
          tag: 186,
          requestKey: 'nutrition_tags',
          requestValue: '186',
          queryKey: 'is_no_sugar',
          pillOrder: 5,
          menuOrder: 5,
        }),
        mf({
          title: 'Paleo-Friendly',
          tag: 145,
          requestKey: 'nutrition_tags',
          requestValue: '145',
          queryKey: 'is_paleo',
          pillOrder: 6,
          menuOrder: 6,
        }),
        mf({
          title: 'Anti-inflammatory',
          tag: 242,
          requestKey: 'nutrition_tags',
          requestValue: '242',
          queryKey: 'is_anti_inflammatory',
          pillOrder: 7,
          menuOrder: 7,
        }),
        mf({
          title: 'Low FODMAP',
          tag: 666,
          requestKey: 'nutrition_tags',
          requestValue: '666',
          queryKey: 'low_fodmap',
          pillOrder: 8,
          menuOrder: 8,
        }),
      ],
    }),
    mf({
      title: 'Breakfast-Friendly',
      type: 'toggle',
      tag: 47,
      requestKey: 'is_breakfast',
      requestValue: '1',
      queryKey: 'is_breakfast',
      queryValue: '1',
      pillOrder: 5,
      menuOrder: 5,
    }),
    mf({
      title: 'Lunch-Friendly',
      type: 'toggle',
      tag: 48,
      requestKey: 'occasion_tags',
      requestValue: '48',
      queryKey: 'is_lunch',
      queryValue: '1',
      pillOrder: 6,
      menuOrder: 6,
    }),
    mf({
      title: 'Newest Additions',
      pillText: 'New',
      type: 'toggle',
      requestKey: 'is_new',
      requestValue: '1',
      queryKey: 'is_new',
      queryValue: '1',
      pillOrder: 7,
      menuOrder: 7,
    }),
    mf({
      title: 'Spice Level',
      pillText: 'Spice',
      type: 'or',
      pillOrder: 8,
      menuOrder: 8,
      options: [
        mf({
          title: 'No Heat',
          tag: 19,
          requestKey: 'spicy_levels',
          requestValue: '0',
          queryKey: 'spicy_level',
          queryValue: '0',
          pillOrder: 1,
          menuOrder: 1,
        }),
        mf({
          title: 'Mild',
          tag: 20,
          requestKey: 'spicy_levels',
          requestValue: '1',
          queryKey: 'spicy_level',
          queryValue: '1',
          pillOrder: 2,
          menuOrder: 2,
        }),
        mf({
          title: 'Medium',
          tag: 21,
          requestKey: 'spicy_levels',
          requestValue: '2',
          queryKey: 'spicy_level',
          queryValue: '2',
          pillOrder: 3,
          menuOrder: 3,
        }),
        mf({
          title: 'Hot',
          tag: 22,
          requestKey: 'spicy_levels',
          requestValue: '3',
          queryKey: 'spicy_level',
          queryValue: '3',
          pillOrder: 4,
          menuOrder: 4,
        }),
      ],
    }),
    mf({
      title: 'Heat and Eat',
      pillText: 'Heat and Eat',
      type: 'toggle',
      tag: 195,
      requestKey: 'marketing_tags',
      requestValue: '195',
      queryKey: 'is_heat_and_eat',
      queryValue: '1',
      pillOrder: 9,
      menuOrder: 9,
    }),
  ],
};

// Flattened leaves: every dropdown option plus each standalone toggle.
function leafFilters(filters: FilterGroup): Filter[] {
  return filters.bottomFilters.flatMap((f) =>
    f.options.length > 0 ? f.options : [f]
  );
}

function isSelected(params: URLSearchParams, f: Filter): boolean {
  return (
    f.queryKey !== null &&
    f.queryValue !== null &&
    params.getAll(f.queryKey).includes(f.queryValue)
  );
}

// Remove a single (queryKey, queryValue) pair, preserving other values on that key.
function removeOption(params: URLSearchParams, f: Filter): void {
  if (f.queryKey === null || f.queryValue === null) {
    return;
  }
  const remaining = params.getAll(f.queryKey).filter((v) => v !== f.queryValue);
  params.delete(f.queryKey);
  remaining.forEach((v) => params.append(f.queryKey as string, v));
}

export function getSelectedMealFilters(
  search: string,
  filters: FilterGroup = mealFilters
): FilterGroup {
  const params = new URLSearchParams(search);
  return {
    topFilters: [],
    query: '',
    bottomFilters: leafFilters(filters).filter((f) => isSelected(params, f)),
  };
}

export function getMealRequestParams(
  search: string,
  filters: FilterGroup = mealFilters
): URLSearchParams {
  const params = new URLSearchParams();
  BASE_PARAMS.forEach(([key, value]) => params.set(key, value));
  getSelectedMealFilters(search, filters).bottomFilters.forEach((f) => {
    if (f.requestKey !== null && f.requestValue !== null) {
      params.append(f.requestKey, f.requestValue);
    }
  });
  return params;
}

export function toggleMealFilter(
  search: string,
  option: Filter
): URLSearchParams {
  const params = new URLSearchParams(search);
  if (option.queryKey === null || option.queryValue === null) {
    return params;
  }
  if (params.getAll(option.queryKey).includes(option.queryValue)) {
    removeOption(params, option);
  } else {
    params.append(option.queryKey, option.queryValue);
  }
  return params;
}

export function clearMealFilter(
  search: string,
  filter: Filter
): URLSearchParams {
  const params = new URLSearchParams(search);
  const targets = filter.options.length > 0 ? filter.options : [filter];
  targets.forEach((t) => removeOption(params, t));
  return params;
}

export function clearAllMealFilters(
  search: string,
  filters: FilterGroup = mealFilters
): URLSearchParams {
  const params = new URLSearchParams(search);
  leafFilters(filters).forEach((f) => removeOption(params, f));
  return params;
}
